import { useEffect, useMemo, useRef, useState } from 'react'

export interface NavigationDisclosureGroup { id?: string; label: string; hidden?: boolean }
export interface NavigationDisclosureOptions {
  host: string
  groups: readonly NavigationDisclosureGroup[]
  activeGroupIndex?: number
  heldGroupIndex?: number
}
export interface NavigationDisclosureController {
  isOpen: (index: number) => boolean
  toggle: (index: number) => void
  open: (index: number) => void
  hoverOpen: (index: number) => void
  cancelHoverOpen: () => void
}

const PREFIX = 'onmarble.navigation.disclosure.v1'
const LEGACY_CONSOLE_KEY = 'onmarble.navigation.collapsed-groups'
const HOVER_OPEN_MS = 420

/** Stable identity wins; a base section's governed label is its legacy identity. */
export function navigationDisclosureKey(group: NavigationDisclosureGroup, index: number): string {
  void index
  return group.id ?? group.label
}

function fallbackIndex(groups: readonly NavigationDisclosureGroup[], active?: number) {
  return active != null && groups[active] && !groups[active]!.hidden
    ? active : groups.findIndex((group) => !group.hidden)
}

function initialOpen(host: string, groups: readonly NavigationDisclosureGroup[], active?: number): Set<string> {
  const fallback = fallbackIndex(groups, active)
  if (typeof window === 'undefined') {
    return fallback < 0 ? new Set() : new Set([navigationDisclosureKey(groups[fallback]!, fallback)])
  }
  const storageKey = `${PREFIX}.${host}`
  try {
    const stored = window.localStorage.getItem(storageKey)
    if (stored != null) {
      const parsed: unknown = JSON.parse(stored)
      const open = new Set(Array.isArray(parsed)
        ? parsed.filter((value): value is string => typeof value === 'string') : [])
      if (Array.isArray(parsed)) return open
    }
    if (host === 'console') {
      const legacy: unknown = JSON.parse(window.localStorage.getItem(LEGACY_CONSOLE_KEY) ?? 'null')
      if (Array.isArray(legacy)) {
        const closed = new Set(legacy.filter((value): value is string => typeof value === 'string'))
        const open = new Set(groups.flatMap((group, index) => {
          const key = navigationDisclosureKey(group, index)
          return closed.has(key) || closed.has(`${group.label}-${index}`) ? [] : [key]
        }))
        window.localStorage.setItem(storageKey, JSON.stringify([...open]))
        return open
      }
    }
  } catch { /* corrupt or unavailable storage is an optional preference */ }
  return fallback < 0 ? new Set() : new Set([navigationDisclosureKey(groups[fallback]!, fallback)])
}

/** Host-scoped, multi-open disclosure with active/held and drag-hover safety. */
export function useNavigationDisclosure({ host, groups, activeGroupIndex, heldGroupIndex }: NavigationDisclosureOptions): NavigationDisclosureController {
  const storageKey = `${PREFIX}.${host}`
  const keys = useMemo(() => groups.map(navigationDisclosureKey), [groups])
  const keySignature = keys.join('\u0000')
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => initialOpen(host, groups, activeGroupIndex))
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const update = (fn: (current: Set<string>) => Set<string>) => setOpenKeys((current) => {
    const next = fn(current)
    try { window.localStorage.setItem(storageKey, JSON.stringify([...next])) } catch { /* optional */ }
    return next
  })
  const open = (index: number) => {
    const key = keys[index]
    if (!key) return
    update((current) => current.has(key) ? current : new Set([...current, key]))
  }

  /* A host commonly boots with its built-in placeholder and replaces that
     group list after the governed navigation read arrives. Preserve stored
     keys the placeholder does not know yet: filtering them would let the
     forced-open placeholder overwrite a person's real disclosure choices.
     Re-read only when the semantic key set changes, not whenever a parent
     creates an equivalent array. The active/held effects then add their keys. */
  useEffect(() => {
    setOpenKeys(initialOpen(host, groups, activeGroupIndex))
  }, [host, keySignature])
  useEffect(() => {
    if (activeGroupIndex != null) open(activeGroupIndex)
    if (heldGroupIndex != null) open(heldGroupIndex)
  }, [activeGroupIndex, heldGroupIndex, keys])
  useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current) }, [])

  return {
    isOpen: (index) => index === activeGroupIndex || index === heldGroupIndex
      || Boolean(keys[index] && openKeys.has(keys[index]!)),
    toggle: (index) => {
      const key = keys[index]
      if (!key || index === activeGroupIndex || index === heldGroupIndex) return
      update((current) => {
        const next = new Set(current)
        if (next.has(key)) next.delete(key); else next.add(key)
        return next
      })
    },
    open,
    hoverOpen: (index) => {
      if (!keys[index] || openKeys.has(keys[index]!)) return
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
      hoverTimer.current = setTimeout(() => open(index), HOVER_OPEN_MS)
    },
    cancelHoverOpen: () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
      hoverTimer.current = null
    },
  }
}
